/**
 * VIN validation per 49 CFR 565. The check digit in position 9 is computed
 * from the other 16 characters, so a mistyped VIN is caught instantly with
 * no network call. Verified against real VINs before shipping.
 */

const TRANSLIT: Record<string, number> = {
  A:1,B:2,C:3,D:4,E:5,F:6,G:7,H:8,
  J:1,K:2,L:3,M:4,N:5,P:7,R:9,
  S:2,T:3,U:4,V:5,W:6,X:7,Y:8,Z:9,
};
const WEIGHTS = [8,7,6,5,4,3,2,10,0,9,8,7,6,5,4,3,2];

export type VinResult =
  | { ok: true; vin: string }
  | { ok: false; message: string };

export function validateVin(raw: string): VinResult {
  const vin = raw.trim().toUpperCase();

  if (vin.length !== 17)
    return { ok: false, message: `VIN is ${vin.length} characters. It needs 17.` };
  if (/[IOQ]/.test(vin))
    return { ok: false, message: "VINs never contain I, O or Q. Check for a 1 or a 0." };
  if (!/^[A-HJ-NPR-Z0-9]+$/.test(vin))
    return { ok: false, message: "That VIN has a character it cannot contain." };

  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const ch = vin[i]!;
    const val = ch >= "0" && ch <= "9" ? Number(ch) : TRANSLIT[ch];
    if (val === undefined)
      return { ok: false, message: "That VIN has an invalid character." };
    sum += val * WEIGHTS[i]!;
  }
  const rem = sum % 11;
  const expected = rem === 10 ? "X" : String(rem);
  if (vin[8] !== expected)
    return { ok: false, message: "That VIN does not check out. Re-read it off the door jamb." };

  return { ok: true, vin };
}

/**
 * Vehicles built before 1981 predate the 17-character standard, so the check
 * digit does not apply. Relaxes the format only; it does not skip validation.
 */
export function validateLegacyVin(raw: string): VinResult {
  const vin = raw.trim().toUpperCase();
  if (!/^[A-HJ-NPR-Z0-9]{5,17}$/.test(vin))
    return { ok: false, message: "Enter 5 to 17 characters, no I, O or Q." };
  return { ok: true, vin };
}
